/* ============================================================
   MONOLITH — motion + interaction system
   GSAP is progressive-enhancement. Everything degrades gracefully.
   ============================================================ */
(function () {
  "use strict";
  const root = document.documentElement;
  root.classList.remove("no-js");
  root.classList.add("js");
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const hasGSAP = typeof window.gsap !== "undefined";
  if (hasGSAP && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  /* ---------- Loader ---------- */
  function runLoader() {
    const loader = document.querySelector("[data-loader]");
    const finish = () => {
      root.classList.add("loaded");
      document.body.style.removeProperty("overflow");
      startReveals();
    };
    if (!loader || reduced) { root.classList.add("loaded"); startReveals(); return; }
    document.body.style.overflow = "hidden";
    const letters = loader.querySelectorAll(".loader__word span");
    const bar = loader.querySelector(".loader__bar");
    if (hasGSAP) {
      const tl = gsap.timeline({ onComplete: finish });
      tl.to(letters, { y: 0, duration: .8, stagger: .04, ease: "expo.out" })
        .to(bar, { width: "100%", duration: 1.1, ease: "expo.inOut" }, "-=.3")
        .to(loader, { autoAlpha: 0, duration: .6, ease: "power2.inOut" }, "+=.1");
    } else {
      letters.forEach((l, i) => setTimeout(() => (l.style.transform = "translateY(0)"), i * 40));
      if (bar) { bar.style.transition = "width 1.1s cubic-bezier(.19,1,.22,1)"; requestAnimationFrame(() => (bar.style.width = "100%")); }
      setTimeout(finish, 1600);
    }
  }

  /* ---------- Reveals ---------- */
  function startReveals() {
    const items = document.querySelectorAll(".reveal, .line-mask-group");
    if (reduced) { items.forEach(i => i.classList.add("is-in")); return; }
    if (hasGSAP && window.ScrollTrigger) {
      items.forEach(el => {
        ScrollTrigger.create({
          trigger: el, start: "top 88%", once: true,
          onEnter: () => el.classList.add("is-in")
        });
      });
      // Parallax
      gsap.utils.toArray("[data-parallax]").forEach(el => {
        const speed = parseFloat(el.dataset.parallax) || .15;
        gsap.to(el, {
          yPercent: speed * 100, ease: "none",
          scrollTrigger: { trigger: el.closest("section") || el, start: "top bottom", end: "bottom top", scrub: true }
        });
      });
      // Count-up stats
      gsap.utils.toArray("[data-count]").forEach(el => {
        const end = parseFloat(el.dataset.count);
        const obj = { v: 0 };
        ScrollTrigger.create({
          trigger: el, start: "top 85%", once: true,
          onEnter: () => gsap.to(obj, {
            v: end, duration: 1.8, ease: "expo.out",
            onUpdate: () => { el.firstChild.nodeValue = Math.round(obj.v).toLocaleString(); }
          })
        });
      });
    } else {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); } });
      }, { threshold: .15, rootMargin: "0px 0px -8% 0px" });
      items.forEach(i => io.observe(i));
    }
  }

  /* ---------- Header ---------- */
  (function header() {
    const h = document.querySelector("[data-header]");
    if (!h) return;
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      h.classList.toggle("is-scrolled", y > 40);
      if (y > 300 && y > last) h.classList.add("is-hidden");
      else h.classList.remove("is-hidden");
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
  })();

  /* ---------- Mobile menu ---------- */
  (function menu() {
    const open = document.querySelector("[data-menu-open]");
    const close = document.querySelector("[data-menu-close]");
    const overlay = document.querySelector("[data-menu]");
    if (!overlay) return;
    const toggle = (state) => {
      overlay.classList.toggle("is-open", state);
      document.body.style.overflow = state ? "hidden" : "";
      overlay.setAttribute("aria-hidden", state ? "false" : "true");
    };
    open && open.addEventListener("click", () => toggle(true));
    close && close.addEventListener("click", () => toggle(false));
    overlay.querySelectorAll("a").forEach(a => a.addEventListener("click", () => toggle(false)));
    document.addEventListener("keydown", e => { if (e.key === "Escape") toggle(false); });
  })();

  /* ---------- Custom cursor + magnetic buttons ---------- */
  (function cursor() {
    if (window.matchMedia("(pointer: coarse)").matches || reduced) return;
    const dot = document.createElement("div");
    dot.className = "cursor"; document.body.appendChild(dot);
    let x = innerWidth / 2, y = innerHeight / 2, cx = x, cy = y;
    addEventListener("mousemove", e => { x = e.clientX; y = e.clientY; });
    (function loop() {
      cx += (x - cx) * .18; cy += (y - cy) * .18;
      dot.style.transform = `translate(${cx}px,${cy}px) translate(-50%,-50%)`;
      requestAnimationFrame(loop);
    })();
    document.querySelectorAll("a,button,[data-magnetic]").forEach(el => {
      el.addEventListener("mouseenter", () => dot.classList.add("is-active"));
      el.addEventListener("mouseleave", () => dot.classList.remove("is-active"));
    });
    document.querySelectorAll("[data-magnetic]").forEach(el => {
      const strength = 22;
      el.addEventListener("mousemove", e => {
        const r = el.getBoundingClientRect();
        const mx = e.clientX - (r.left + r.width / 2);
        const my = e.clientY - (r.top + r.height / 2);
        el.style.transform = `translate(${(mx / r.width) * strength}px,${(my / r.height) * strength}px)`;
      });
      el.addEventListener("mouseleave", () => { el.style.transform = ""; });
    });
  })();

  /* ---------- Reading progress ---------- */
  (function progress() {
    const bar = document.querySelector("[data-reading-progress]");
    const article = document.querySelector("[data-article-body]");
    if (!bar || !article) return;
    const onScroll = () => {
      const rect = article.getBoundingClientRect();
      const total = rect.height - innerHeight;
      const done = Math.min(1, Math.max(0, -rect.top / total));
      bar.style.width = (done * 100) + "%";
    };
    addEventListener("scroll", onScroll, { passive: true }); onScroll();
  })();

  /* ---------- Quantity + AJAX cart count ---------- */
  (function qty() {
    document.querySelectorAll("[data-qty]").forEach(w => {
      const input = w.querySelector("input");
      w.querySelectorAll("button").forEach(b => b.addEventListener("click", () => {
        const step = b.dataset.step === "up" ? 1 : -1;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }));
    });
  })();

  async function refreshCartCount() {
    try {
      const r = await fetch("/cart.js", { headers: { "Accept": "application/json" } });
      const c = await r.json();
      document.querySelectorAll("[data-cart-count]").forEach(el => {
        el.textContent = c.item_count;
        el.style.display = c.item_count > 0 ? "grid" : "none";
      });
    } catch (e) { /* offline / editor */ }
  }
  window.MONOLITH = { refreshCartCount, startReveals };

  /* ---------- Boot ---------- */
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", runLoader);
  else runLoader();

  // Shopify Theme Editor: re-run reveals when sections load
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", startReveals);
    root.classList.add("loaded");
  }
})();
